// src/components/Plugins.js
import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Container,
  Chip,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Edit, 
  Logout, 
  Dashboard, 
  Extension,
  PhotoCamera,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Search,
  Computer,
  Code,
} from '@mui/icons-material';
import { ref, set, get, child, remove, update } from 'firebase/database';
import { auth, database, storage } from '../firebase/Config';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

// Função para gerar código de requisição no formato XXXXX-XXXXX
const generateRequestCode = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = [];
  for (let i = 0; i < 2; i++) {
    let segment = '';
    for (let j = 0; j < 5; j++) {
      segment += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    segments.push(segment);
  }
  return segments.join('-');
};

const Plugins = () => {
  const [plugins, setPlugins] = useState([]);
  const [filteredPlugins, setFilteredPlugins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPlugin, setEditingPlugin] = useState(null);
  const [newPlugin, setNewPlugin] = useState({
    name: '', platform: '', requestCode: generateRequestCode(), logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  // Estados para validação e feedback
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    pluginId: null,
    pluginName: '',
  });

  // Monitorar a autenticação com onAuthStateChanged
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email);
      } else {
        setUserEmail(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Funções de feedback
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Validação de formulário
  const validateForm = () => {
    const newErrors = {};

    if (!newPlugin.name || !newPlugin.name.trim()) {
      newErrors.name = 'Nome do software é obrigatório';
    } else if (newPlugin.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (!isEditing) {
      // Verificar se já existe plugin com este nome
      const existingPlugin = plugins.find(
        plugin => (plugin.name || '').toLowerCase() === newPlugin.name.toLowerCase()
      );
      if (existingPlugin) {
        newErrors.name = 'Já existe um plugin com este nome';
      }
    }

    if (!newPlugin.platform) {
      newErrors.platform = 'Plataforma é obrigatória';
    }

    if (!newPlugin.requestCode || !newPlugin.requestCode.trim()) {
      newErrors.requestCode = 'Código de requisição é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setLogoPreview(null);
    setNewPlugin({ name: '', platform: '', requestCode: generateRequestCode(), logo: null });
    setIsEditing(false);
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
  };

  const fetchPlugins = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'plugins'));
      if (snapshot.exists()) {
        const pluginsData = snapshot.val();
        const pluginsArray = Object.keys(pluginsData).map((key) => ({
          id: key,
          ...pluginsData[key],
        }));
        setPlugins(pluginsArray);
        setFilteredPlugins(pluginsArray);
      } else {
        setPlugins([]);
        setFilteredPlugins([]);
      }
    } catch (error) {
      console.error('Erro ao buscar plugins: ', error);
      showSnackbar('Erro ao carregar plugins', 'error');
    }
    setLoading(false);
  };

  const handleAddPlugin = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const pluginId = newPlugin.requestCode;

      let logoURL = '';
      if (newPlugin.logo) {
        const logoRef = storageRef(storage, `plugins/${pluginId}/logo.jpg`);
        await uploadBytes(logoRef, newPlugin.logo);
        logoURL = await getDownloadURL(logoRef);
      }

      await set(ref(database, `plugins/${pluginId}`), {
        id: pluginId,
        name: newPlugin.name.trim(),
        platform: newPlugin.platform,
        requestCode: newPlugin.requestCode,
        logoURL: logoURL || '',
      });

      showSnackbar('Plugin cadastrado com sucesso!', 'success');
      fetchPlugins();
      handleClose();
    } catch (error) {
      console.error('Erro ao criar plugin: ', error);
      showSnackbar('Erro ao cadastrar plugin', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditPlugin = (plugin) => {
    setIsEditing(true);
    setEditingPlugin(plugin);
    setNewPlugin({
      name: plugin.name || '',
      platform: plugin.platform || '',
      requestCode: plugin.requestCode || '',
      logo: null,
    });
    setLogoPreview(plugin.logoURL || null);
    setErrors({});
    setOpen(true);
  };

  const handleSaveEditPlugin = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const pluginId = editingPlugin.id;
      let logoURL = editingPlugin.logoURL;

      if (newPlugin.logo) {
        const logoRef = storageRef(storage, `plugins/${pluginId}/logo.jpg`);
        await uploadBytes(logoRef, newPlugin.logo);
        logoURL = await getDownloadURL(logoRef);
      }

      logoURL = logoURL || editingPlugin.logoURL || '';

      await update(ref(database, `plugins/${pluginId}`), {
        name: newPlugin.name.trim(),
        platform: newPlugin.platform,
        requestCode: newPlugin.requestCode,
        logoURL: logoURL,
      });

      showSnackbar('Plugin atualizado com sucesso!', 'success');
      fetchPlugins();
      handleClose();
    } catch (error) {
      console.error('Erro ao atualizar plugin: ', error);
      showSnackbar('Erro ao atualizar plugin', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenDeleteDialog = (plugin) => {
    setDeleteDialog({
      open: true,
      pluginId: plugin.id,
      pluginName: plugin.name,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, pluginId: null, pluginName: '' });
  };

  const handleDeletePlugin = async () => {
    try {
      await remove(ref(database, `plugins/${deleteDialog.pluginId}`));
      showSnackbar('Plugin excluído com sucesso!', 'success');
      fetchPlugins();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao deletar plugin: ', error);
      showSnackbar('Erro ao excluir plugin', 'error');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validar tipo de arquivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        showSnackbar('Por favor, selecione uma imagem válida (JPG, PNG ou GIF)', 'error');
        return;
      }

      // Validar tamanho (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('A imagem deve ter no máximo 5MB', 'error');
        return;
      }

      setNewPlugin({ ...newPlugin, logo: file });
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Função de pesquisa
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === '') {
      setFilteredPlugins(plugins);
    } else {
      const filtered = plugins.filter((plugin) => {
        const name = (plugin.name || '').toLowerCase();
        const platform = (plugin.platform || '').toLowerCase();
        const requestCode = (plugin.requestCode || '').toLowerCase();
        
        return (
          name.includes(value) ||
          platform.includes(value) ||
          requestCode.includes(value)
        );
      });
      setFilteredPlugins(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredPlugins(plugins);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchPlugins();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const platforms = ['Windows', 'Android', 'AutoCAD', 'AutoCAD Civil 3D', 'Navisworks', 'Advance Steel', 'Revit'];

  const columns = [
    {
      field: 'logoURL',
      headerName: 'Logo',
      width: 100,
      renderCell: (params) => (
        <Avatar 
          src={params.value} 
          alt="Logo do Plugin"
          sx={{ 
            width: 45, 
            height: 45,
            border: '2px solid #f6ad55',
          }}
        >
          <Extension />
        </Avatar>
      ),
    },
    { field: 'name', headerName: 'Nome', width: 200 },
    { field: 'platform', headerName: 'Plataforma', width: 180 },
    { field: 'requestCode', headerName: 'Código de Requisição', width: 200 },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Editar plugin">
            <IconButton 
              onClick={() => handleEditPlugin(params.row)}
              sx={{ 
                color: '#f6ad55',
                '&:hover': { bgcolor: '#f6ad5520' }
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir plugin">
            <IconButton 
              onClick={() => handleOpenDeleteDialog(params.row)}
              sx={{ 
                color: '#e53935',
                '&:hover': { bgcolor: '#e5393520' }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(90deg, #ec4899 0%, #db2777 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton 
            onClick={() => navigate('/home')}
            sx={{ 
              color: '#fff', 
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Gerenciamento de Plugins
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#ec4899' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: '#fff',
                  '&:hover': { 
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <Extension sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
                    Lista de Plugins
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de plugins cadastrados: <strong>{plugins.length}</strong>
                    {searchTerm && ` | Filtrados: ${filteredPlugins.length}`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(246, 173, 85, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(246, 173, 85, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Cadastrar Novo Plugin
                </Button>
              </Box>

              {/* Barra de Pesquisa */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar por nome, plataforma ou código..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ color: '#718096' }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={filteredPlugins}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Cadastro/Edição */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  {isEditing ? 'Editar Plugin' : 'Cadastrar Novo Plugin'}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  alt="Logo do Plugin"
                  src={logoPreview}
                  sx={{ 
                    width: 120, 
                    height: 120,
                    mb: 2,
                    border: '4px solid #f6ad55',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Extension sx={{ fontSize: 48 }} />
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  sx={{
                    borderColor: '#f6ad55',
                    color: '#f6ad55',
                    '&:hover': {
                      borderColor: '#ed8936',
                      bgcolor: '#f6ad5510',
                    },
                  }}
                >
                  Carregar Logo
                  <input 
                    type="file" 
                    hidden 
                    accept="image/*"
                    onChange={handleLogoChange} 
                  />
                </Button>
                <Typography variant="caption" sx={{ color: '#718096', mt: 1 }}>
                  JPG, PNG ou GIF (máx. 5MB)
                </Typography>
              </Box>

              <TextField
                label="Nome do Software"
                fullWidth
                margin="normal"
                required
                value={newPlugin.name}
                onChange={(e) => {
                  setNewPlugin({ ...newPlugin, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: null });
                }}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Extension sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl 
                fullWidth 
                margin="normal"
                error={!!errors.platform}
                required
              >
                <InputLabel>Plataforma</InputLabel>
                <Select
                  value={newPlugin.platform}
                  label="Plataforma"
                  onChange={(e) => {
                    setNewPlugin({ ...newPlugin, platform: e.target.value });
                    if (errors.platform) setErrors({ ...errors, platform: null });
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Computer sx={{ color: '#718096', ml: 1 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Selecione uma plataforma</em>
                  </MenuItem>
                  {platforms.map((platform) => (
                    <MenuItem key={platform} value={platform}>
                      {platform}
                    </MenuItem>
                  ))}
                </Select>
                {errors.platform && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                    {errors.platform}
                  </Typography>
                )}
              </FormControl>

              <TextField
                label="Código de Requisição"
                fullWidth
                margin="normal"
                required
                value={newPlugin.requestCode}
                onChange={(e) => {
                  setNewPlugin({ ...newPlugin, requestCode: e.target.value });
                  if (errors.requestCode) setErrors({ ...errors, requestCode: null });
                }}
                error={!!errors.requestCode}
                helperText={errors.requestCode || 'Código único para identificação'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Code sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={isEditing ? handleSaveEditPlugin : handleAddPlugin}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(246, 173, 85, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      {isEditing ? (
                        <>
                          <CheckCircle sx={{ mr: 1 }} />
                          Salvar Alterações
                        </>
                      ) : (
                        <>
                          <Add sx={{ mr: 1 }} />
                          Adicionar Plugin
                        </>
                      )}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o plugin <strong>{deleteDialog.pluginName}</strong>? 
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeletePlugin}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Plugins;
